import React, { useState, useEffect } from 'react';
import { Calendar, dayjsLocalizer } from 'react-big-calendar';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import 'dayjs/locale/es-do';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import {
	Modal,
	Button,
	Box,
	Paper,
	Typography,
	IconButton,
	useTheme,
	useMediaQuery,
	Drawer,
	AppBar,
	Toolbar,
	Divider
} from '@mui/material';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import CloseIcon from '@mui/icons-material/Close';
import EventIcon from '@mui/icons-material/Event';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';

dayjs.extend(relativeTime);
dayjs.locale('es-do');
const localizer = dayjsLocalizer(dayjs);

const EventComponent = ({ event }) => (
	<Box sx={{
		backgroundColor: '#ffd6e7',
		border: '1px solid #ff85c0',
		borderRadius: '4px',
		p: 1,
		mt: 0.5,
		fontSize: { xs: '0.75rem', sm: '0.85rem' },
		boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
		'&:hover': {
			boxShadow: '0 4px 8px rgba(0,0,0,0.15)',
		}
	}}>
		<Typography variant="subtitle2" fontWeight="bold" sx={{ color: '#c41d7f' }}>
			{event.title}
		</Typography>
		<Typography variant="body2" sx={{ color: '#666', fontSize: { xs: '0.7rem', sm: '0.8rem' } }}>
			{event.description}
		</Typography>
	</Box>
);

function CustomCalendar({ open, onClose, onSelectSlot, events, handleCurrentView, isMobile }) {
	const theme = useTheme();

	const isMonday = (date) => {
		return dayjs(date).day() === 1;
	};

	const isPastDate = (date) => {
		return dayjs(date).isBefore(dayjs(), 'day');
	}

	const handleSlotClick = (slotInfo) => {
		if (!isMonday(slotInfo.start) && !isPastDate(slotInfo.start)) {
			onSelectSlot(slotInfo);
		}
	};

	const dayPropGetter = (date) => {
		if (isMonday(date) || isPastDate(date)) {
			return {
				style: {
					backgroundColor: '#f0f0f0',
					color: '#ccc',
					cursor: 'not-allowed'
				}
			};
		}
		return {};
	};

	// Vista móvil con Drawer
	if (isMobile) {
		return (
			<Drawer
				anchor="bottom"
				open={open}
				onClose={onClose}
				PaperProps={{
					sx: {
						height: '90vh',
						borderTopLeftRadius: '16px',
						borderTopRightRadius: '16px',
						overflowY: 'hidden'
					}
				}}
			>
				<AppBar position="static" sx={{ bgcolor: '#ffadd2', boxShadow: 'none' }}>
					<Toolbar>
						<Typography variant="h6" component="div" sx={{ flexGrow: 1, color: '#fff' }}>
							Seleccionar fecha del evento
						</Typography>
						<IconButton edge="end" color="inherit" onClick={onClose} aria-label="cerrar">
							<CloseIcon />
						</IconButton>
					</Toolbar>
				</AppBar>

				<Box sx={{ p: 2, height: 'calc(90vh - 64px)' }}>
					<Calendar
						localizer={localizer}
						events={events}
						views={['month', 'day']} // Limitamos vistas para móvil
						defaultView="month"
						selectable
						onSelectSlot={handleSlotClick}
						style={{
							height: '100%',
							width: '100%'
						}}
						step={30}
						timeslots={2}
						min={new Date(2024, 8, 22, 8, 0)}
						max={new Date(2024, 8, 22, 23, 0)}
						eventPropGetter={() => ({
							style: {
								backgroundColor: '#eb2f96',
								color: '#fff',
								borderRadius: '4px',
								border: 'none'
							}
						})}
						dayPropGetter={dayPropGetter}
						onView={handleCurrentView}
						messages={{
							next: "Siguiente",
							previous: "Anterior",
							today: "Hoy",
							month: "Mes",
							day: "Día",
							noEventsInRange: "No hay eventos programados"
						}}
						components={{
							event: EventComponent
						}}
						formats={{
							dayFormat: (date, culture, localizer) =>
								localizer.format(date, 'dd', culture),
							dayHeaderFormat: (date, culture, localizer) =>
								localizer.format(date, 'ddd DD', culture)
						}}
					/>
				</Box>
			</Drawer>
		);
	}

	// Vista desktop con Modal
	return (
		<Modal
			open={open}
			onClose={onClose}
			aria-labelledby="calendar-modal-title"
		>
			<Paper
				elevation={3}
				sx={{
					position: 'absolute',
					top: '50%',
					left: '50%',
					transform: 'translate(-50%, -50%)',
					width: { sm: '80%', md: '70%' },
					maxWidth: '1000px',
					height: { sm: '70vh', md: '80vh' },
					borderRadius: 2,
					p: 3,
					overflow: 'hidden'
				}}
			>
				<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
					<Typography id="calendar-modal-title" variant="h5" component="h2" sx={{ color: '#eb2f96', fontWeight: 'bold' }}>
						Seleccionar fecha del evento
					</Typography>
					<IconButton onClick={onClose} size="large" sx={{ color: '#eb2f96' }}>
						<CloseIcon />
					</IconButton>
				</Box>

				<Divider sx={{ mb: 2 }} />

				<Box sx={{ height: 'calc(100% - 70px)' }}>
					<Calendar
						localizer={localizer}
						events={events}
						views={['month', 'week', 'day', 'agenda']}
						selectable
						onSelectSlot={handleSlotClick}
						style={{
							height: '100%',
							width: '100%'
						}}
						step={30}
						timeslots={2}
						min={new Date(2024, 8, 22, 8, 0)}
						max={new Date(2024, 8, 22, 23, 0)}
						eventPropGetter={() => ({
							style: {
								backgroundColor: '#eb2f96',
								color: '#fff',
								borderRadius: '4px'
							}
						})}
						dayPropGetter={dayPropGetter}
						onView={handleCurrentView}
						messages={{
							next: "Siguiente",
							previous: "Anterior",
							today: "Hoy",
							month: "Mes",
							week: "Semana",
							day: "Día",
							agenda: "Agenda",
							noEventsInRange: "No hay eventos programados"
						}}
						components={{
							event: EventComponent
						}}
					/>
				</Box>
			</Paper>
		</Modal>
	);
}

export default function SelectEventDate({ labelName, handleEventDate, typeOfEvent, eventDate, showDateTimePicker }) {
	const [open, setOpen] = useState(false);
	const [selectedDate, setSelectedDate] = useState(eventDate ? dayjs(eventDate) : dayjs());
	const [newEvent, setNewEvent] = useState(null);
	const [currentView, setCurrentView] = useState('month');
	const [events, setEvents] = useState([]);

	// Detectar si estamos en móvil
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

	const handleOpen = () => setOpen(true);
	const handleClose = () => setOpen(false);

	const handleCurrentView = (view) => {
		setCurrentView(view);
	}

	const listEvents = async () => {
		try {
			const response = await fetch('https://dprincess-training.servehttp.com/events', {
				method: 'GET',
				headers: {
					'Authorization': `Bearer ${localStorage.getItem('token')}`
				}
			});

			if (!response.ok) {
				throw new Error("Error en la solicitud");
			}

			const data = await response.json();
			const eventInside = data.map((event) => (
				event.eventType === 'inside' &&
				{
					start: dayjs(event.eventDateStart).toDate(),
					end: dayjs(event.eventDateEnd).toDate(),
					title: event.eventTitle,
					description: event.eventDescription,
				}
			));

			const eventOutside = data.map(event => (
				event.eventType === 'outside' &&
				{
					start: dayjs(event.eventDateStart).toDate(),
					end: dayjs(event.eventDateEnd).toDate(),
					title: event.eventTitle + ' (estudio)',
					description: event.eventDescription ? event.eventDescription : "Sesión fotográfica en el estudio"
				}
			));

			if (typeOfEvent === 'inside') {
				return setEvents([...eventInside]);
			} else if (typeOfEvent === 'outside') {
				return setEvents([...eventOutside]);
			}
		} catch (error) {
			console.error(error);
		}
	}

	const handleSelectSlot = ({ start, end }) => {
		if (currentView === 'day' || currentView === 'week') {
			const isOverlapping = events.some(event =>
				(start < event.end && end > event.start)
			);

			const sinceStart = dayjs(start).from(end);

			if (sinceStart === 'hace 30 minutos' || sinceStart === 'hace una hora') {
				const minMinutes = 90;
				end = dayjs(end).add(minMinutes, 'minutes').toDate();
			}

			if (!isOverlapping) {
				const event = {
					start,
					end,
					title: 'Nuevo Evento'
				};
				setNewEvent(event);
				if (!newEvent) {
					setEvents([...events, event]);
					setSelectedDate(dayjs(start));
					setCurrentView('month');
					handleEventDate(event);
					return handleClose();
				} else {
					let eventsMinus = events;
					eventsMinus.pop();
					setEvents([...eventsMinus, event]);
					setSelectedDate(dayjs(start));
					setCurrentView('month');
					handleEventDate(event);
					return handleClose();
				}
			}
		}
	}

	useEffect(() => {
		listEvents();
	}, []);

	return (
		<LocalizationProvider dateAdapter={AdapterDayjs}>
			<Box
				sx={{
					display: 'flex',
					flexDirection: isMobile ? 'column' : 'row',
					alignItems: isMobile ? 'flex-start' : 'center',
					gap: 1,
					width: '100%'
				}}
			>
				{!showDateTimePicker && (
					<Box
						sx={{
							flex: 1,
							width: isMobile ? '100%' : 'auto',
							mb: isMobile ? 1 : 0
						}}
					>
						<DateTimePicker
							label={labelName}
							value={selectedDate}
							onChange={(newValue) => setSelectedDate(newValue)}
							readOnly
							sx={{
								width: '100%',
								'& .MuiInputBase-root': {
									fontSize: isMobile ? '0.9rem' : '1rem',
									height: isMobile ? '54px' : '56px'
								},
								'& .MuiInputLabel-root': {
									fontSize: isMobile ? '0.85rem' : '0.9rem'
								}
							}}
							slotProps={{
								textField: {
									fullWidth: true
								}
							}}
						/>
					</Box>
				)}

				<Button
					variant={isMobile ? "contained" : "outlined"}
					onClick={handleOpen}
					startIcon={<EventIcon />}
					sx={{
						minWidth: isMobile ? '100%' : '120px',
						backgroundColor: isMobile ? '#eb2f96' : 'transparent',
						borderColor: '#eb2f96',
						color: isMobile ? '#fff' : '#eb2f96',
						'&:hover': {
							backgroundColor: isMobile ? '#c41d7f' : 'rgba(235, 47, 150, 0.08)',
							borderColor: '#c41d7f'
						},
						py: isMobile ? 1.5 : 1,
						borderRadius: 1.5,
						boxShadow: isMobile ? '0px 4px 8px rgba(0, 0, 0, 0.1)' : 'none'
					}}
				>
					{isMobile ? 'Seleccionar fecha' : 'Abrir calendario'}
				</Button>

				<CustomCalendar
					open={open}
					onClose={handleClose}
					onSelectSlot={handleSelectSlot}
					events={events}
					handleCurrentView={handleCurrentView}
					isMobile={isMobile}
				/>
			</Box>
		</LocalizationProvider>
	);
}